import React, { createContext, useState, useContext, useEffect } from "react";
import { getUserProfile } from "utils/auth";
import { useParams, useLocation } from "react-router-dom";
import { fetchEncounterById } from "utils/api";

const WizardContext = createContext({});

export const WizardProvider = ({ children }) => {
  const formDataName = "bioSpecimenFormData";
  const [formValid, setFormValid] = useState(false);
  const [profile, setProfile] = useState(null);
  const [encounter, setEncounter] = useState({});
  const [patient, setPatient] = useState({});
  const [studies, setStudies] = useState([]);
  const [consents, setConsents] = useState([]);

  const { encounterId } = useParams();
  const location = useLocation();
  const emptyFormData = {
    encounterId: encounterId,
    kitNumber: "",
    signValue: "",
  };

  const [formData, setFormData] = useState(() => {
    try {
      const storedData = sessionStorage.getItem(formDataName);
      return storedData ? JSON.parse(storedData) : emptyFormData;
    } catch (error) {
      console.error("Error parsing stored data:", error);
      return {};
    }
  });

  useEffect(() => {
    // Check if encounter data was passed via navigation state
    const encounterData = location.state;

    if (encounterData && encounterData.encounter) {
      // Use the new data structure passed from SampleCollection
      console.log(
        "🔍 Using encounter data from navigation state:",
        encounterData
      );
      setEncounter(encounterData.encounter);
      setPatient(encounterData.patient || {});
      setStudies(encounterData.studies || []);
      setConsents(encounterData.consents || []);
    } else {
      // Fallback to old API call if no data passed
      console.log(
        "⚠️ No encounter data in navigation state, falling back to API call"
      );
      const fetchEncounter = async () => {
        try {
          const encounter = await fetchEncounterById(encounterId);
          setEncounter(encounter || {});
        } catch (err) {
          console.error(err);
          setEncounter({});
        }
      };
      fetchEncounter();
    }
  }, [encounterId, location.state]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userProfile = await getUserProfile();
        setProfile(userProfile);
      } catch (err) {
        console.error(err);
        setProfile({});
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    sessionStorage.setItem(formDataName, JSON.stringify(formData));
  }, [formData]);

  const clearFormData = () => {
    sessionStorage.setItem(formDataName, {});
  };

  const updateFormData = (newData) => {
    setFormData((prevData) => ({ ...prevData, ...newData }));
  };

  return (
    <WizardContext.Provider
      value={{
        formData,
        clearFormData,
        updateFormData,
        formValid,
        setFormValid,
        profile,
        encounter,
        patient,
        studies,
        consents,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
};

export const useWizardContext = () => useContext(WizardContext);
